import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  getAllArticles, 
  deleteArticle, 
  toggleArticlePublishStatus 
} from '../../../services/firebase/blogService';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../../services/firebase/config';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiEye, 
  FiCheck, 
  FiX,
  FiSearch,
  FiRefreshCw 
} from 'react-icons/fi';

const BlogManagement = () => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  const loadArticles = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const articles = await getAllArticles();
      setArticles(articles);
      setFilteredArticles(articles);
    } catch (error) {
      console.error('Makaleler yüklenirken hata:', error);
      toast.error('Makaleleri yüklerken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [user]); // user bağımlılığını ekleyin

  useEffect(() => {
    loadArticles();
  }, [loadArticles]); // loadArticles fonksiyonunu bağımlılık olarak ekleyin

  useEffect(() => {
    if (user) {
      loadArticles();
    }
  }, [user, loadArticles]);

  // Makale silme
  const handleDeleteArticle = async (id, title) => {
    if (!window.confirm(`"${title}" makalesini silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await deleteArticle(id);
      
      // Listeyi güncelle
      setArticles(prevArticles => prevArticles.filter(article => article.id !== id));
      setFilteredArticles(prevArticles => prevArticles.filter(article => article.id !== id));
      
      toast.success('Makale başarıyla silindi');
    } catch (error) {
      console.error('Makale silinirken hata:', error);
      toast.error('Makale silinirken bir hata oluştu');
    }
  };

  // Makale yayınlama durumunu değiştir
  const handleTogglePublishStatus = async (id, currentStatus, title) => {
    try {
      const newStatus = !currentStatus;
      await toggleArticlePublishStatus(id, newStatus);
      
      // Listeyi güncelle
      setArticles(prevArticles => prevArticles.map(article => 
        article.id === id ? { ...article, isPublished: newStatus } : article
      ));
      setFilteredArticles(prevArticles => prevArticles.map(article => 
        article.id === id ? { ...article, isPublished: newStatus } : article
      ));
      
      toast.success(`"${title}" ${newStatus ? 'yayınlandı' : 'taslak olarak ayarlandı'}`);
    } catch (error) {
      console.error('Makale durumu değiştirilirken hata:', error);
      toast.error('Makale durumu değiştirilirken bir hata oluştu');
    }
  };

  // Filtreleme
  useEffect(() => {
    let filtered = [...articles];
    
    // Arama terimlerine göre filtrele
    if (searchTerm) {
      filtered = filtered.filter(article => 
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.slug.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Duruma göre filtrele
    if (statusFilter !== 'all') {
      filtered = filtered.filter(article => 
        statusFilter === 'published' ? article.isPublished : !article.isPublished
      );
    }
    
    setFilteredArticles(filtered);
  }, [searchTerm, statusFilter, articles]);

  // Tarih formatla
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Tarih formatla (yakın tarih için)
  const formatRelativeDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000; // saniye cinsinden fark
    
    if (diff < 60) {
      return 'az önce';
    } else if (diff < 3600) {
      const minutes = Math.floor(diff / 60);
      return `${minutes} dakika önce`;
    } else if (diff < 86400) {
      const hours = Math.floor(diff / 3600);
      return `${hours} saat önce`;
    } else if (diff < 604800) {
      const days = Math.floor(diff / 86400);
      return `${days} gün önce`;
    } else {
      return formatDate(timestamp);
    }
  };

  return (
    <div className="container mx-auto px-4 py-20">
      <div className="mb-6 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Eğitim/Blog Makaleleri Yönetimi</h1>
          <Link
            to="/admin/blog/create"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center whitespace-nowrap"
          >
            <FiPlus className="mr-2" />
            Yeni Makale
          </Link>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg mb-8 max-w-6xl mx-auto">
        {/* Filtreleme ve arama */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between space-y-2 sm:space-y-0 sm:space-x-2">
          <div className="flex items-center space-x-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Arama..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <button
              onClick={() => setSearchTerm('')}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <FiX />
            </button>
          </div>
          
          <div className="flex space-x-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block border border-gray-300 rounded-md leading-5 bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm py-2 px-3"
            >
              <option value="all">Tüm Makaleler</option>
              <option value="published">Yayında</option>
              <option value="draft">Taslak</option>
            </select>
            
            <button
              onClick={loadArticles}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <FiRefreshCw />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/3">
                    Başlık
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/4">
                    Oluşturma Tarihi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/4">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredArticles.length > 0 ? (
                  filteredArticles.map((article) => (
                    <tr key={article.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {article.imageUrl && (
                            <div className="flex-shrink-0 h-10 w-10 mr-3">
                              <img
                                className="h-10 w-10 rounded-md object-cover"
                                src={article.imageUrl}
                                alt={article.title}
                              />
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {article.title}
                            </div>
                            {article.viewCount !== undefined && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {article.viewCount || 0} görüntülenme
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* Slug hücresi kaldırıldı */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatRelativeDate(article.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          article.isPublished
                            ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                        }`}>
                          {article.isPublished ? 'Yayında' : 'Taslak'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <a
                            href={`/egitim/${article.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                            title="Görüntüle"
                          >
                            <FiEye className="h-5 w-5" />
                          </a>
                          <button
                            onClick={() => handleTogglePublishStatus(article.id, article.isPublished, article.title)}
                            className={`${
                              article.isPublished
                                ? 'text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300'
                                : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                            }`}
                            title={article.isPublished ? 'Taslağa Dönüştür' : 'Yayınla'}
                          >
                            {article.isPublished ? (
                              <FiX className="h-5 w-5" />
                            ) : (
                              <FiCheck className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={() => navigate(`/admin/blog/edit/${article.id}`)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Düzenle"
                          >
                            <FiEdit2 className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteArticle(article.id, article.title)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Sil"
                          >
                            <FiTrash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'Arama kriterlerine uygun makale bulunamadı'
                        : 'Henüz makale bulunmuyor'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogManagement;